import { Button, RadioButton } from '@idexx-laboratories/spot-react';
import { Action, Transition } from 'history';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Grid from '../../components/Grid/Grid';
import Loader from '../../components/Loader/Loader';
import QualificationIcon from '../../components/QualificationIcon/QualificationIcon';
import { useBlocker } from '../../hooks/use-blocker';
import { useStore } from '../../hooks/use-store';
import { isDefined } from '../../utils/object-utils';
import './Confirmation.scss';

const MAX_CONTACTS = 4;

const RescheduleUpgradePrompt = () => (
  <>
    Plans change, we get it. Click <strong>Reschedule Upgrade</strong> to complete rescheduling
    steps. Your original details are retained if you click <strong>Keep Upgrade</strong>
  </>
);

const Confirmation = observer(() => {
  const { confirmationStore: store, globalStore } = useStore();
  const {
    scheduleDate,
    statistics,
    currentContacts,
    colDefs,
    buttonsDisabled,
    notificationLoader,
    cancelLoader,
    saveLoader,
    practice,
    operationSuccessful,
    acknowledgementsLastAcceptedDate,
    upgradeSummaryReportUrl,
    acknowledgementsReportUrl,
  } = store;
  const { upgradeScheduled } = globalStore;
  const navigate = useNavigate();

  useEffect(() => {
    store.init();
    return () => store.close();
  }, [navigate, store]);

  useBlocker(
    useCallback(
      (tx: Transition): void => {
        if (tx.action !== Action.Pop || !upgradeScheduled) tx.retry();
      },
      [upgradeScheduled],
    ),
  );

  return (
    <div className='confirmation-wrapper'>
      <div className='spot-typography__heading--level-2'>
        Confirmation - {upgradeScheduled ? 'Summary' : 'Schedule'} for{' '}
        <span title={globalStore.selectedSap.name}>{globalStore.selectedSap.id}&apos;s</span>{' '}
        {practice?.toCsVersion.version} upgrade
      </div>
      <div className='spot-typography__text--tertiary'>
        Here are your upgrade schedule details and Summary report. <br />
        You can also adjust and resend notifications, reschedule or even cancel your upgrade.
      </div>
      <div className='confirmation_body'>
        <dl className='steps-list'>
          <dt>
            Step 1 - Acknowledgements Reviewed{' '}
            <QualificationIcon pass={upgradeScheduled || operationSuccessful} />
          </dt>
          <dd>
            {acknowledgementsLastAcceptedDate} by{' '}
            {practice.acknowledgementsLastAccepted?.name || ''}
          </dd>
          <dt>
            Step 2 - Schedule Data and Time{' '}
            <QualificationIcon pass={upgradeScheduled || operationSuccessful} />
          </dt>
          <dd>
            {scheduleDate} by {practice.scheduledBy || ''}
          </dd>
          <dt>
            Step 3 - Check Qualifications <QualificationIcon pass />
          </dt>
          <dd>Upgrade - {statistics.upgradeServers} server (Qualified)</dd>
          <dd>Upgrade - {statistics.upgradeWorkstations} workstations (Qualified)</dd>
          <dd>Do Not Upgrade - {statistics.doNotUpgrade} computers</dd>
          <dt>Step 4 - Notifications {upgradeScheduled && <QualificationIcon pass />}</dt>
          <dd>
            <Button
              className='add-more-btn'
              aria-label='Add More'
              variant='link'
              size='small'
              iconLeft='plus'
              disabled={store.currentContacts.length >= MAX_CONTACTS}
              onClick={(e: any) => {
                store.addContact();
                e.stopPropagation();
              }}
              data-testid='add-contact-btn'
            >
              Add more
            </Button>
            <span className='gray-note'>(You may add up to 3 contacts)</span>
            <Grid id='notifications-grid' columns={colDefs} data={currentContacts} zebraRows />
          </dd>
          <dt>Step 5 - Upgrade Options</dt>
          <dd>
            <RadioButton
              id='skip-offline-workstations'
              label='Proceed with upgrade if workstations are offline'
              className='upgrade-option'
              aria-label='Skip Offline Workstations'
              checked={practice.skippingWorkstationsAllowed === true}
              disabled={upgradeScheduled}
              onChange={() =>
                runInAction(
                  () =>
                    (practice.skippingWorkstationsAllowed = !practice.skippingWorkstationsAllowed),
                )
              }
            />
            <ul>
              <li>
                Continue with upgrade if any workstations are offline at your chosen upgrade time.
              </li>
              <li>If the server is offline, the upgrade will not continue.</li>
              <li>
                A report will be sent to the contacts above indicating which workstations were not
                upgraded.
              </li>
            </ul>
          </dd>
          <dd>
            <RadioButton
              id='dont-skip-offline-workstations'
              label='Upgrade only if all selected computers are online'
              className='upgrade-option'
              aria-label="Don't Skip Offline Workstations"
              checked={practice.skippingWorkstationsAllowed === false}
              disabled={upgradeScheduled}
              onChange={() =>
                runInAction(
                  () =>
                    (practice.skippingWorkstationsAllowed = !practice.skippingWorkstationsAllowed),
                )
              }
            />
            <ul>
              <li>
                If any computers selected for upgrade are offline at your chosen time, the upgrade
                will not take place.
              </li>
              <li>
                A message will be sent to the contacts above to notify them the upgrade did not
                proceed.
              </li>
            </ul>
          </dd>
          {(upgradeScheduled || !operationSuccessful) && upgradeSummaryReportUrl && (
            <>
              <dt className='doc-header'>
                Upgrade Summary Report{' '}
                <QualificationIcon pass={operationSuccessful}></QualificationIcon>
              </dt>
              {!operationSuccessful && (
                <>
                  <dd className='doc-error-message'>
                    {upgradeScheduled
                      ? 'A problem occurred when removing the current scheduled upgrade on some of your computers.'
                      : 'A problem occurred when scheduling your upgrade.'}
                  </dd>
                  <dd className='doc-error-message last'>
                    See the attached reports for details and next steps.
                  </dd>
                </>
              )}
              <dd>
                <a
                  href={upgradeSummaryReportUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='doc-link'
                >
                  <img
                    src='/images/pdf_document_icon.svg'
                    className='doc-icon'
                    alt='PDF icon'
                    title='Click to view the report'
                  />
                </a>
              </dd>
              {acknowledgementsReportUrl && (
                <>
                  <dt className='doc-header'>
                    Acknowledgments Review{' '}
                    <QualificationIcon pass={operationSuccessful}></QualificationIcon>
                  </dt>
                  <dd>
                    <a
                      href={acknowledgementsReportUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='doc-link'
                    >
                      <img
                        src='/images/pdf_document_icon.svg'
                        className='doc-icon'
                        alt='PDF icon'
                        title='Acknowledgements Document'
                      />
                    </a>
                  </dd>
                </>
              )}
            </>
          )}
        </dl>
        <div className='page-bottom-actions'>
          <div>
            {upgradeScheduled ? (
              <>
                <Button
                  variant='link'
                  size='small'
                  disabled={buttonsDisabled}
                  onClick={() => store.promptCancelUpgrade()}
                  data-testid='cancel-btn'
                >
                  <Loader loading={cancelLoader}>Cancel Upgrade</Loader>
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant='link'
                  size='small'
                  onClick={() => globalStore.showExitUpgradePopup(() => store.exitUpgrade())}
                  data-testid='exit-btn'
                >
                  Exit Upgrade
                </Button>
                <Button
                  variant='link'
                  size='small'
                  onClick={() => navigate(-1)}
                  data-testid='back-btn'
                >
                  Back
                </Button>
              </>
            )}
          </div>
          <div>
            {upgradeScheduled ? (
              <>
                <Button
                  variant='secondary'
                  size='small'
                  disabled={buttonsDisabled}
                  onClick={() => store.promptRescheduleUpgrade(RescheduleUpgradePrompt)}
                >
                  <Loader loading={notificationLoader}>Reschedule Upgrade</Loader>
                </Button>
                <Button
                  variant='secondary'
                  size='small'
                  disabled={buttonsDisabled}
                  onClick={() => void store.resendNotifications()}
                >
                  <Loader loading={notificationLoader}>Resend Notifications</Loader>
                </Button>
                <Button
                  variant='primary'
                  size='small'
                  disabled={buttonsDisabled}
                  onClick={() => navigate('/')}
                >
                  <Loader loading={notificationLoader}>Dashboard</Loader>
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant='secondary'
                  size='small'
                  disabled={buttonsDisabled}
                  onClick={() => void store.saveForLater()}
                >
                  <Loader loading={saveLoader}>Save for later</Loader>
                </Button>
                <Button
                  variant='primary'
                  size='small'
                  disabled={buttonsDisabled || !isDefined(practice.skippingWorkstationsAllowed)}
                  onClick={() => void store.scheduleAndNotify()}
                >
                  <Loader loading={notificationLoader}>Schedule and Notify</Loader>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default Confirmation;
